// Core multi-tenant types for CommunityApp

export interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  customDomain?: string;
  logoUrl?: string;
  primaryColor: string;
  accentColor: string;
  status: 'active' | 'inactive' | 'suspended';
  subscriptionTier: 'basic' | 'premium' | 'enterprise';
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  tenantId: string;
  email: string;
  firstName: string;
  lastName: string;
  role:
    | 'super_admin'
    | 'community_admin'
    | 'admin'
    | 'moderator'
    | 'resident'
    | 'vendor'
    | 'city_official'
    | 'emergency_coordinator'
    | 'department_head'
    | 'bank_lender'
    | 'platform_support'
    | 'toolboxlogic_community_sp_elect'
    | 'toolboxlogic_community_subpanel'
    | 'toolboxlogic_community_support'
    | 'toolboxlogic_community_sales';
  status: 'active' | 'pending' | 'inactive' | 'suspended';
  phone?: string;
  unitNumber?: string;
  building?: string;
  avatarUrl?: string;
  digitalId?: string;
  kycVerified: boolean;
  emergencyContact?: EmergencyContact;
  preferences: UserPreferences;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
  email?: string;
}

export interface UserPreferences {
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
    emergencyOnly: boolean;
  };
  theme: 'light' | 'dark' | 'system';
  language: string;
  timezone: string;
}

export interface AuthUser {
  id: string;
  tenantId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: User['role'];
  permissions: string[];
  avatarUrl?: string;
  entityType?: string; // Entity context for proper view routing (board, manager, resident, etc.)
}

export interface JWTPayload {
  userId: string;
  tenantId: string;
  email: string;
  role: User['role'];
  permissions: string[];
  exp: number;
  iat: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
  tenantId?: string; // For multi-tenant login
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  unitNumber?: string;
  building?: string;
  phone?: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordReset {
  token: string;
  newPassword: string;
}

// API Response types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: ApiError;
  meta?: {
    pagination?: Pagination;
    filters?: Record<string, unknown>;
  };
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  timestamp: string;
  requestId: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// Permission constants
export const PERMISSIONS = {
  // User Management
  'users.view': 'View users',
  'users.create': 'Create users',
  'users.edit': 'Edit users',
  'users.delete': 'Delete users',
  'users.manage_roles': 'Manage user roles',

  // Tenant Management
  'tenant.view': 'View tenant settings',
  'tenant.edit': 'Edit tenant settings',
  'tenant.modules': 'Configure modules',

  // Governance
  'governance.vote': 'Vote in elections',
  'governance.create_proposals': 'Create proposals',
  'governance.manage_elections': 'Manage elections',
  'governance.view_results': 'View voting results',

  // Finance
  'finance.view_wallet': 'View wallet',
  'finance.transfer': 'Transfer funds',
  'finance.view_treasury': 'View treasury',
  'finance.manage_treasury': 'Manage treasury',
  'finance.create_invoices': 'Create invoices',

  // Marketplace
  'marketplace.create_listings': 'Create marketplace listings',
  'marketplace.view_listings': 'View marketplace listings',
  'marketplace.manage_listings': 'Manage all listings',

  // Facilities
  'facilities.book': 'Book facilities',
  'facilities.manage': 'Manage facilities',
  'facilities.view_bookings': 'View all bookings',

  // Maintenance
  'maintenance.report': 'Report maintenance issues',
  'maintenance.manage': 'Manage work orders',
  'maintenance.assign': 'Assign work orders',

  // Communication
  'communication.send_messages': 'Send messages',
  'communication.announcements': 'Create announcements',
  'communication.emergency_alerts': 'Create emergency alerts',

  // Documents
  'documents.upload': 'Upload documents',
  'documents.view': 'View documents',
  'documents.manage': 'Manage all documents',

  // AI Assistant
  'ai.basic': 'Basic AI assistant access',
  'ai.advanced': 'Advanced AI features',

  // Bank/Lending
  'bank.view_proposals': 'View loan proposals',
  'bank.evaluate_proposals': 'Evaluate and review proposals',
  'bank.approve_loans': 'Approve loan applications',
  'bank.manage_collateral': 'Manage collateral assessments',
  'bank.pricing': 'Set and modify loan pricing',
  'bank.integration': 'Manage bank integration settings',
  'bank.analytics': 'View bank analytics and reports',
  'bank.risk_assessment': 'Perform risk assessments',
} as const;

export type Permission = keyof typeof PERMISSIONS;

// Role-based permissions mapping
export const ROLE_PERMISSIONS: Record<User['role'], Permission[]> = {
  super_admin: Object.keys(PERMISSIONS) as Permission[],
  admin: [
    'users.view',
    'users.create',
    'users.edit',
    'users.manage_roles',
    'tenant.view',
    'tenant.edit',
    'tenant.modules',
    'governance.manage_elections',
    'governance.view_results',
    'finance.view_treasury',
    'finance.manage_treasury',
    'finance.create_invoices',
    'marketplace.manage_listings',
    'facilities.manage',
    'facilities.view_bookings',
    'maintenance.manage',
    'maintenance.assign',
    'communication.announcements',
    'communication.emergency_alerts',
    'documents.manage',
    'ai.advanced',
  ],
  moderator: [
    'users.view',
    'governance.vote',
    'governance.create_proposals',
    'finance.view_wallet',
    'finance.transfer',
    'finance.view_treasury',
    'marketplace.create_listings',
    'marketplace.view_listings',
    'facilities.book',
    'facilities.view_bookings',
    'maintenance.report',
    'maintenance.manage',
    'communication.send_messages',
    'documents.upload',
    'documents.view',
    'ai.basic',
  ],
  vendor: [
    'marketplace.create_listings',
    'marketplace.view_listings',
    'maintenance.manage',
    'maintenance.report',
    'communication.send_messages',
    'documents.upload',
    'documents.view',
    'ai.basic',
  ],
  resident: [
    'governance.vote',
    'finance.view_wallet',
    'finance.transfer',
    'marketplace.create_listings',
    'marketplace.view_listings',
    'facilities.book',
    'maintenance.report',
    'communication.send_messages',
    'documents.upload',
    'documents.view',
    'ai.basic',
  ],
  city_official: [
    'users.view',
    'governance.manage_elections',
    'governance.view_results',
    'finance.view_treasury',
    'facilities.manage',
    'facilities.view_bookings',
    'maintenance.manage',
    'maintenance.assign',
    'communication.announcements',
    'communication.emergency_alerts',
    'documents.manage',
    'ai.advanced',
  ],
  emergency_coordinator: [
    'users.view',
    'maintenance.manage',
    'maintenance.assign',
    'communication.emergency_alerts',
    'documents.manage',
    'ai.advanced',
  ],
  department_head: [
    'users.view',
    'governance.view_results',
    'finance.view_treasury',
    'facilities.manage',
    'facilities.view_bookings',
    'maintenance.manage',
    'maintenance.assign',
    'communication.announcements',
    'documents.manage',
    'ai.advanced',
  ],
  bank_lender: [
    'bank.view_proposals',
    'bank.evaluate_proposals',
    'bank.approve_loans',
    'bank.manage_collateral',
    'bank.pricing',
    'bank.integration',
    'bank.analytics',
    'bank.risk_assessment',
    'documents.view',
    'documents.manage',
    'ai.advanced',
  ],
  platform_support: [
    'users.view',
    'users.create',
    'users.edit',
    'tenant.view',
    'tenant.edit',
    'governance.view_results',
    'finance.view_treasury',
    'finance.create_invoices',
    'marketplace.view_listings',
    'marketplace.manage_listings',
    'facilities.view_bookings',
    'maintenance.manage',
    'maintenance.assign',
    'communication.send_messages',
    'communication.announcements',
    'documents.view',
    'documents.upload',
    'documents.manage',
    'ai.advanced',
  ],
  // Platform roles for ToolboxLogic internal staff
  community_admin: Object.keys(PERMISSIONS) as Permission[], // Same as super_admin
  toolboxlogic_community_sp_elect: Object.keys(PERMISSIONS) as Permission[], // Supreme authority
  toolboxlogic_community_subpanel: Object.keys(PERMISSIONS) as Permission[], // Engineering/ops team
  toolboxlogic_community_support: Object.keys(PERMISSIONS) as Permission[], // Customer success
  toolboxlogic_community_sales: Object.keys(PERMISSIONS) as Permission[], // Sales team
};

export type UserRole = User['role'];
export type UserStatus = User['status'];
export type TenantStatus = Tenant['status'];
export type SubscriptionTier = Tenant['subscriptionTier'];
